import {View} from 'react-native'
import Animated from 'react-native-reanimated'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {HITSLOP_10} from '#/lib/constants'
import {PressableScale} from '#/lib/custom-animations/PressableScale'
import {useHaptics} from '#/lib/haptics'
import {useMinimalShellHeaderTransform} from '#/lib/hooks/useMinimalShellTransform'
import {isWeb} from '#/platform/detection'
import {emitSoftReset} from '#/state/events'
import {useSession} from '#/state/session'
import {useShellLayout} from '#/state/shell/shell-layout'
import {Logo} from '#/view/icons/Logo'
import {atoms as a, select, useBreakpoints, useTheme, web} from '#/alf'
import {ButtonIcon} from '#/components/Button'
import {Hashtag_Stroke2_Corner0_Rounded as FeedsIcon} from '#/components/icons/Hashtag'
import * as Layout from '#/components/Layout'
import {Link} from '#/components/Link'

export function HomeHeaderLayoutMobile({
  children,
  variant = 'default',
}: {
  children: React.ReactNode
  tabBarAnchor: JSX.Element | null | undefined
  variant?: 'default' | 'gallery'
}) {
  const t = useTheme()
  const {headerHeight} = useShellLayout()
  const headerMinimalShellTransform = useMinimalShellHeaderTransform()
  const {hasSession} = useSession()
  const {gtMobile, gtTablet} = useBreakpoints()
  const playHaptic = useHaptics()
  const {_} = useLingui()

  const glassBackground = select(t.name, {
    light: 'rgba(255,255,255,0.65)',
    dark: 'rgba(18,18,18,0.55)',
    dim: 'rgba(18,18,18,0.55)',
  })
  const glassBorder = select(t.name, {
    light: 'rgba(0,0,0,0.06)',
    dark: 'rgba(255,255,255,0.08)',
    dim: 'rgba(255,255,255,0.08)',
  })
  const glassShadow = select(t.name, {
    light: '0 8px 24px rgba(0,0,0,0.06)',
    dark: 'none',
    dim: 'none',
  })
  const headerMaxWidth = gtTablet ? 600 : gtMobile ? 720 : '100%'
  const headerSurfaceStyles = isWeb
    ? [
        {
          backgroundColor: glassBackground,
          borderBottomWidth: 1,
          borderBottomColor: glassBorder,
        },
        web({
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          boxShadow: glassShadow,
        }),
      ]
    : t.atoms.bg

  return (
    <Animated.View
      style={[
        a.fixed,
        a.z_10,
        {
          top: 0,
          left: 0,
          right: 0,
        },
        headerSurfaceStyles,
        headerMinimalShellTransform,
      ]}
      onLayout={e => {
        const nextHeight = e.nativeEvent.layout.height
        headerHeight.set(nextHeight)
        if (isWeb && typeof document !== 'undefined') {
          document.documentElement.style.setProperty(
            '--header-h',
            `${Math.max(0, Math.round(nextHeight))}px`,
          )
        }
      }}>
      {variant === 'gallery' && isWeb ? (
        <>
          <View
            style={[a.w_full, web({paddingTop: 'env(safe-area-inset-top)'})]}>
            <View style={[a.w_full, a.mx_auto, {maxWidth: headerMaxWidth}]}>
              {children}
            </View>
          </View>
        </>
      ) : (
        <>
          <Layout.Header.Outer noBottomBorder>
            <Layout.Header.Slot>
              <Layout.Header.MenuButton />
            </Layout.Header.Slot>

            <View style={[a.flex_1, a.align_center]}>
              <PressableScale
                targetScale={0.9}
                onPress={() => {
                  playHaptic('Light')
                  emitSoftReset()
                }}>
                <Logo width={30} />
              </PressableScale>
            </View>

            <Layout.Header.Slot>
              {hasSession && (
                <Link
                  testID="viewHeaderHomeFeedPrefsBtn"
                  to={{screen: 'Feeds'}}
                  hitSlop={HITSLOP_10}
                  label={_(msg`View your feeds and explore more`)}
                  size="small"
                  variant="ghost"
                  color="secondary"
                  shape="square"
                  style={[
                    a.justify_center,
                    {marginRight: -Layout.BUTTON_VISUAL_ALIGNMENT_OFFSET},
                  ]}>
                  <ButtonIcon icon={FeedsIcon} size="lg" />
                </Link>
              )}
            </Layout.Header.Slot>
          </Layout.Header.Outer>
          {children}
        </>
      )}
    </Animated.View>
  )
}
